import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL_WITH_API } from '../config/api';
import { getEffectiveToken, getEffectiveUser } from '../utils/authSession';
import { FEEDBACK_MODAL_EVENT, FeedbackModalSource, FeedbackModalStep } from '../utils/feedbackModal';
import { MENU_ICON_PATHS } from './menuIcons';

type FeedbackStep = 'main' | 'thanks' | 'issue' | 'feature';
type FeedbackModalOpenDetail = {
  initialStep?: FeedbackModalStep;
  returnToRoute?: string;
  source?: FeedbackModalSource;
};

const EMOJI_SCALE = [
  { value: 1, emoji: '😫' },
  { value: 2, emoji: '😧' },
  { value: 3, emoji: '😐' },
  { value: 4, emoji: '😀' },
  { value: 5, emoji: '😃' }
];

const FeedbackModalHost = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<FeedbackStep>('main');
  const [rating, setRating] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [issueExplanation, setIssueExplanation] = useState('');
  const [suggestedFeature, setSuggestedFeature] = useState('');
  const [attachments, setAttachments] = useState<Array<{
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    dataBase64: string;
  }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [returnToRoute, setReturnToRoute] = useState<string | null>(null);
  const [openedFromAutoPrompt, setOpenedFromAutoPrompt] = useState(false);
  const [autoPromptSubmitted, setAutoPromptSubmitted] = useState(false);
  const [hoveredAction, setHoveredAction] = useState<'issue' | 'feature' | null>(null);
  const [activeAction, setActiveAction] = useState<'issue' | 'feature' | null>(null);

  const resolveInitialStep = (value?: FeedbackModalStep): FeedbackStep => {
    if (value === 'issue' || value === 'feature') return value;
    return 'main';
  };

  const user = useMemo(() => getEffectiveUser(), [open]);
  const isSuperAdmin = Boolean(user?.isSuperAdmin);

  const normalizeRoute = (route: string) => {
    let normalized = route.trim();
    if (!normalized) return '';
    if (normalized.startsWith('#')) {
      normalized = normalized.slice(1);
    }
    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }
    return normalized;
  };

  const navigateToRoute = (route: string | null) => {
    if (!route) return;
    const normalized = normalizeRoute(route);
    if (!normalized) return;
    const nextHash = `#${normalized}`;
    if (window.location.hash !== nextHash) {
      window.location.hash = nextHash;
    }
  };

  const closeAndMaybeReturn = (shouldReturn = true) => {
    if (saving) return;
    if (openedFromAutoPrompt && !autoPromptSubmitted) {
      try {
        window.postMessage(
          {
            source: 'trafficlite-web',
            action: 'FEEDBACK_PROMPT_MARK_DISMISSED'
          },
          window.location.origin
        );
      } catch (error) {
        console.warn('Could not mark feedback prompt dismissed:', error);
      }
    }
    const routeToReturn = shouldReturn ? returnToRoute : null;
    setOpen(false);
    setReturnToRoute(null);
    setOpenedFromAutoPrompt(false);
    setAutoPromptSubmitted(false);
    navigateToRoute(routeToReturn);
  };

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const effectiveUser = getEffectiveUser();
      if (effectiveUser?.isSuperAdmin) {
        return;
      }
      const detail = (event as CustomEvent<FeedbackModalOpenDetail>)?.detail;
      const nextReturnRoute = typeof detail?.returnToRoute === 'string' && detail.returnToRoute.trim()
        ? normalizeRoute(detail.returnToRoute)
        : null;
      setOpen(true);
      setStep(resolveInitialStep(detail?.initialStep));
      setRating(null);
      setIssueExplanation('');
      setSuggestedFeature('');
      setAttachments([]);
      setError(null);
      setSuccessMessage(null);
      setName(String(effectiveUser?.name || ''));
      setEmail(String(effectiveUser?.email || ''));
      setReturnToRoute(nextReturnRoute);
      setOpenedFromAutoPrompt(detail?.source === 'auto');
      setAutoPromptSubmitted(false);
    };

    window.addEventListener(FEEDBACK_MODAL_EVENT, handleOpen as EventListener);
    return () => {
      window.removeEventListener(FEEDBACK_MODAL_EVENT, handleOpen as EventListener);
    };
  }, []);

  if (isSuperAdmin) {
    return null;
  }

  const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (attachments.length >= 5) {
      setError('You can upload up to 5 attachments.');
      event.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Each attachment must be 8MB or less.');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : '';
      if (!base64) {
        setError('Could not read attachment.');
        return;
      }
      setAttachments((prev) => ([
        ...prev,
        {
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          dataBase64: base64
        }
      ]));
      setError(null);
    };
    reader.onerror = () => {
      setError('Could not read attachment.');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const removeAttachmentAtIndex = (index: number) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const closeModal = () => {
    closeAndMaybeReturn(true);
  };

  const submitFeedback = async (feedbackType: 'main' | 'issue' | 'feature') => {
    if (feedbackType === 'main' && !rating) return;
    const token = getEffectiveToken();
    if (!token) {
      setError('Not authenticated. Please sign in again.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL_WITH_API}/feedback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          feedbackType,
          rating: rating ?? null,
          name: name.trim(),
          email: email.trim().toLowerCase(),
          issueExplanation: feedbackType === 'issue' ? issueExplanation.trim() : '',
          suggestedFeature: feedbackType === 'feature' ? suggestedFeature.trim() : '',
          attachments: (feedbackType === 'issue' || feedbackType === 'feature') ? attachments : []
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit feedback');
      }
      if (openedFromAutoPrompt) {
        setAutoPromptSubmitted(true);
        try {
          window.postMessage(
            {
              source: 'trafficlite-web',
              action: 'FEEDBACK_PROMPT_MARK_SUBMITTED'
            },
            window.location.origin
          );
        } catch (markError) {
          console.warn('Could not mark feedback prompt submitted:', markError);
        }
      }
      if (feedbackType === 'main') {
        setError(null);
        setSuccessMessage(null);
        setStep('thanks');
        return;
      }
      setSuccessMessage('Thank you! Your feedback was submitted.');
      setTimeout(() => closeAndMaybeReturn(true), 900);
    } catch (submitError: unknown) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to submit feedback');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200
      }}
    >
      <div
        style={{
          width: '560px',
          position: 'relative',
          background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          border: '1px solid #dbeafe'
        }}
      >
        <button
          type="button"
          onClick={closeModal}
          aria-label="Close feedback modal"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '26px',
            height: '26px',
            borderRadius: '9999px',
            border: '1px solid #d1d5db',
            backgroundColor: 'white',
            color: '#374151',
            fontSize: '15px',
            fontWeight: 700,
            lineHeight: 1,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1
          }}
          disabled={saving}
        >
          ×
        </button>
        <h3
          className="traffic-heading-font"
          style={{
            marginTop: 0,
            marginBottom: '10px',
            fontWeight: 800,
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <img src="/icon.png" alt="" aria-hidden="true" style={{ width: '16px', height: '16px', objectFit: 'contain' }} />
          <span>
            {step === 'main'
              ? 'How is Traffic Lite working for you?'
              : (step === 'thanks'
                ? 'Thank you for your feedback!'
                : (step === 'issue' ? 'Report an Issue' : 'Suggest a Feature'))}
          </span>
        </h3>

        {step === 'main' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', marginBottom: '14px' }}>
              {EMOJI_SCALE.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setRating(item.value)}
                  style={{
                    border: rating === item.value ? '2px solid #2563eb' : '1px solid #d1d5db',
                    backgroundColor: rating === item.value ? '#eff6ff' : 'white',
                    borderRadius: '8px',
                    width: '56px',
                    height: '56px',
                    fontSize: '26px',
                    cursor: 'pointer'
                  }}
                  aria-label={`Rate ${item.value}`}
                >
                  {item.emoji}
                </button>
              ))}
            </div>
          </>
        )}

        {(step === 'issue' || step === 'feature') && (
          <div style={{ display: 'grid', gap: '10px', marginBottom: '12px' }}>
            <label style={{ fontSize: '14px', color: '#374151', fontWeight: 700 }}>Name:</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white' }}
            />
            <label style={{ fontSize: '14px', color: '#374151', fontWeight: 700 }}>Email:</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white' }}
            />
            <label style={{ fontSize: '14px', color: '#374151', fontWeight: 700 }}>
              {step === 'issue' ? 'Explanation of Issue:' : 'Suggested feature:'}
            </label>
            <textarea
              value={step === 'issue' ? issueExplanation : suggestedFeature}
              onChange={(e) => {
                if (step === 'issue') setIssueExplanation(e.target.value);
                if (step === 'feature') setSuggestedFeature(e.target.value);
              }}
              rows={4}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical', backgroundColor: 'white' }}
            />
            <div style={{ marginTop: '4px', color: '#4b5563', fontSize: '13px' }}>
              Rating selected: <strong>{rating ?? 'none'}</strong> {rating ? '/ 5' : ''}
            </div>
          </div>
        )}

        <div style={{ marginBottom: '8px', minHeight: '20px' }}>
          {error && <div style={{ color: '#b91c1c', fontSize: '13px' }}>{error}</div>}
          {successMessage && <div style={{ color: '#166534', fontSize: '13px' }}>{successMessage}</div>}
        </div>

        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
          {step === 'main' ? (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                onClick={() => void submitFeedback('main')}
                disabled={saving || !rating}
                style={{
                  minWidth: '120px',
                  padding: '7px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: rating ? '#4285f4' : '#6b7280',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: rating ? 'pointer' : 'not-allowed',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          ) : step === 'thanks' ? (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setStep('issue')}
                onMouseEnter={() => setHoveredAction('issue')}
                onMouseLeave={() => {
                  setHoveredAction((prev) => (prev === 'issue' ? null : prev));
                  setActiveAction((prev) => (prev === 'issue' ? null : prev));
                }}
                onMouseDown={() => setActiveAction('issue')}
                onMouseUp={() => setActiveAction((prev) => (prev === 'issue' ? null : prev))}
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: activeAction === 'issue' ? '1px solid #ef4444' : '1px solid #111827',
                  backgroundColor: activeAction === 'issue'
                    ? '#fee2e2'
                    : (hoveredAction === 'issue' ? '#f8fafc' : '#ffffff'),
                  color: activeAction === 'issue' ? '#991b1b' : '#111827',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease'
                }}
              >
                <img src={MENU_ICON_PATHS.reportIssue} alt="" style={{ width: '16px', height: '16px', objectFit: 'contain' }} />
                Report an Issue
              </button>
              <button
                type="button"
                onClick={() => setStep('feature')}
                onMouseEnter={() => setHoveredAction('feature')}
                onMouseLeave={() => {
                  setHoveredAction((prev) => (prev === 'feature' ? null : prev));
                  setActiveAction((prev) => (prev === 'feature' ? null : prev));
                }}
                onMouseDown={() => setActiveAction('feature')}
                onMouseUp={() => setActiveAction((prev) => (prev === 'feature' ? null : prev))}
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: activeAction === 'feature' ? '1px solid #22c55e' : '1px solid #111827',
                  backgroundColor: activeAction === 'feature'
                    ? '#dcfce7'
                    : (hoveredAction === 'feature' ? '#f8fafc' : '#ffffff'),
                  color: activeAction === 'feature' ? '#166534' : '#111827',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease'
                }}
              >
                <img src={MENU_ICON_PATHS.suggestFeature} alt="" style={{ width: '16px', height: '16px', objectFit: 'contain' }} />
                Suggest a Feature
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep('thanks')}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 700
                }}
              >
                Back
              </button>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flex: 1, minWidth: 0, paddingRight: '10px' }}>
                {attachments.map((attachment, index) => (
                  <span
                    key={`${attachment.fileName}-${index}`}
                    title={attachment.fileName}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      maxWidth: '130px',
                      padding: '4px 8px',
                      borderRadius: '999px',
                      backgroundColor: '#eef2ff',
                      color: '#1f2937',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{attachment.fileName}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachmentAtIndex(index)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        lineHeight: 1
                      }}
                      aria-label={`Remove ${attachment.fileName}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label
                  htmlFor={`feedback-attachment-input-${step}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '36px',
                    borderRadius: '0',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: attachments.length >= 5 ? 'not-allowed' : 'pointer',
                    opacity: attachments.length >= 5 ? 0.6 : 1
                  }}
                  title="Add attachment"
                >
                  <img
                    src={MENU_ICON_PATHS.upload}
                    alt="Attach"
                    style={{ height: '24px', width: '24px', objectFit: 'contain' }}
                  />
                </label>
                <input
                  id={`feedback-attachment-input-${step}`}
                  type="file"
                  onChange={handleAttachmentChange}
                  disabled={attachments.length >= 5}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => void submitFeedback(step)}
                  disabled={saving}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: '#4285f4',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    opacity: saving ? 0.7 : 1
                  }}
                >
                  {saving ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackModalHost;
